import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Snackbar,
  Alert,
  Chip,
  CircularProgress,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { getAssistant, getAssistantData, updateAssistantData } from '../services/assistantService';

export default function AssistantDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [assistant, setAssistant] = useState(null);
  const [csvData, setCsvData] = useState(null);
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    loadAssistantData();
  }, [id]);

  const loadAssistantData = async () => {
    setLoading(true);
    try {
      // Get assistant info
      const assistantInfo = await getAssistant(id);
      if (!assistantInfo) {
        setSnackbar({
          open: true,
          message: 'Assistant not found',
          severity: 'error',
        });
        setTimeout(() => navigate('/assistant'), 2000);
        return;
      }
      setAssistant(assistantInfo);

      // Get CSV data
      const data = await getAssistantData(id);
      if (!data) {
        setSnackbar({
          open: true,
          message: 'No data found for this assistant',
          severity: 'error',
        });
        return;
      }
      setCsvData(data);
    } catch (error) {
      console.error('Error loading assistant data:', error);
      setSnackbar({
        open: true,
        message: 'Error loading data',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCellClick = (rowIndex, columnName, currentValue) => {
    setEditingCell({ rowIndex, columnName });
    setEditValue(currentValue || '');
  };

  const handleCellChange = (e) => {
    setEditValue(e.target.value);
  };

  const handleCellSave = async () => {
    if (!editingCell) return;

    try {
      setSaving(true);

      // Get the row data
      const row = csvData.rows[editingCell.rowIndex];
      const rowId = row.rowId;

      // Call API to update the cell
      await updateAssistantData(id, rowId, editingCell.columnName, editValue);

      // Update local state
      const newData = { ...csvData };
      newData.rows[editingCell.rowIndex].data[editingCell.columnName] = editValue;
      setCsvData(newData);

      setSnackbar({
        open: true,
        message: 'Cell updated successfully',
        severity: 'success',
      });

      setEditingCell(null);
      setEditValue('');
    } catch (error) {
      console.error('Error saving cell:', error);
      setSnackbar({
        open: true,
        message: 'Error saving changes',
        severity: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCellCancel = () => {
    setEditingCell(null);
    setEditValue('');
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!assistant || !csvData) {
    return (
      <Box>
        <Typography variant="h6" color="error">
          Assistant or data not found
        </Typography>
        <Button onClick={() => navigate('/assistant')} sx={{ mt: 2 }}>
          Back to Assistants
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={() => navigate('/assistant')} sx={{ mr: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h4" sx={{ fontWeight: 600 }}>
            {assistant.name}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              File: {assistant.fileName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Rows: {assistant.rowCount}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Created: {formatDate(assistant.createdAt)}
            </Typography>
            <Chip
              label={assistant.status}
              color={assistant.status === 'active' ? 'success' : 'default'}
              size="small"
            />
          </Box>
        </Box>
      </Box>

      {/* Editable Table */}
      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer sx={{ maxHeight: 'calc(100vh - 250px)' }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, backgroundColor: '#1e1e1e' }}>
                  Row
                </TableCell>
                {csvData.headers.map((header) => (
                  <TableCell key={header} sx={{ fontWeight: 600, backgroundColor: '#1e1e1e' }}>
                    {header}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {csvData.rows.map((row, rowIndex) => (
                <TableRow key={rowIndex} hover>
                  <TableCell sx={{ backgroundColor: '#1e1e1e', fontWeight: 500 }}>
                    {rowIndex + 1}
                  </TableCell>
                  {csvData.headers.map((header) => {
                    const isEditing = editingCell?.rowIndex === rowIndex && editingCell?.columnName === header;
                    const cellValue = row.data[header];

                    return (
                      <TableCell
                        key={`${rowIndex}-${header}`}
                        onClick={() => !isEditing && handleCellClick(rowIndex, header, cellValue)}
                        sx={{
                          cursor: isEditing ? 'default' : 'pointer',
                          '&:hover': {
                            backgroundColor: isEditing ? 'transparent' : 'rgba(25, 118, 210, 0.08)',
                          },
                          padding: isEditing ? '8px' : '16px',
                        }}
                      >
                        {isEditing ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <TextField
                              value={editValue}
                              onChange={handleCellChange}
                              size="small"
                              autoFocus
                              fullWidth
                              disabled={saving}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  handleCellSave();
                                } else if (e.key === 'Escape') {
                                  handleCellCancel();
                                }
                              }}
                            />
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={handleCellSave}
                              disabled={saving}
                            >
                              {saving ? <CircularProgress size={20} /> : <CheckIcon />}
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={handleCellCancel}
                              disabled={saving}
                            >
                              <CloseIcon />
                            </IconButton>
                          </Box>
                        ) : (
                          cellValue || '-'
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Instructions */}
      <Box sx={{ mt: 2 }}>
        <Typography variant="caption" color="text.secondary">
          Click on any cell to edit its value. Press Enter to save or Escape to cancel.
        </Typography>
      </Box>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
